import { FC, useContext, useMemo, useEffect, useState, useCallback, useRef, useSyncExternalStore, SVGProps } from 'react';
import { useOnValueChange } from '../../../hooks/useOnValueChange';
import { RendererContext } from '../../../contexts/RendererContext';
import { usePersonaManager } from '../../../hooks/usePersonaManager';
import { ToolButton } from '../../../components/ToolButton';
import { Tooltip } from '../../../components/Tooltip';
import { T } from '../../../translation/src';

// Icon imports
import SelectIcon from '../../../assets/icons/select.svg?react';
import LocationIcon from '../../../assets/icons/location.svg?react';
import LineIcon from '../../../assets/icons/vector-line.svg?react';
import PolygonIcon from '../../../assets/icons/vector-polygon.svg?react';
import CubeIcon from '../../../assets/icons/cube.svg?react';
import ClusterIcon from '../../../assets/icons/magic-wand.svg?react';
import PlaneIcon from '../../../assets/icons/plane.svg?react';
import ScreenshotIcon from '../../../assets/icons/screenshot.svg?react';
import UndoIcon from '../../../assets/icons/undo.svg?react';
import RedoIcon from '../../../assets/icons/redo.svg?react';
import FloorDetectionAutomatischIcon from '../../../assets/icons/Floor-detection-automatisch.svg?react';
import FloorDetectionHalfautomatischIcon from '../../../assets/icons/Floor-detection-halfautomatisch.svg?react';
import UndoDotIcon from '../../../assets/icons/undo-dot.svg?react';
import RoomDetectionAutomatischIcon from '../../../assets/icons/Room-detection-automatisch.svg?react';
import RoomDetectionHalfautomatischIcon from '../../../assets/icons/Room-detection-halfautomatisch.svg?react';
import RoomDetectionManueelIcon from '../../../assets/icons/Room-detection-manueel.svg?react';
import SurfaceDetectionAutomatischIcon from '../../../assets/icons/surface-detection-automatisch.svg?react';
import SurfaceDetectionmanueelIcon from '../../../assets/icons/surface-detection-manueel.svg?react';
import WallSketchIcon from '../../../assets/icons/wall-sketch.svg?react';
import WallCreateIcon from '../../../assets/icons/wall_create.svg?react';
import WallEditIcon from '../../../assets/icons/wall_edit.svg?react';
import BeamAutomatischIcon from '../../../assets/icons/Beam_automatisch.svg?react';
import ColumnAutomatischIcon from '../../../assets/icons/Column_automatisch.svg?react';
import ColumnIcon from '../../../assets/icons/Column.svg?react';
import BeamIcon from '../../../assets/icons/Beam.svg?react';
import DoorIcon from '../../../assets/icons/door.svg?react';
import ChairIcon from '../../../assets/icons/chair.svg?react';
import RoomIcon from '../../../assets/icons/Room.svg?react';
import MergeRoomsIcon from '../../../assets/icons/merge-rooms.svg?react';
import RulerIcon from '../../../assets/icons/ruler.svg?react';

/** Stable empty set so an empty snapshot keeps its identity across reads. */
const EMPTY_DISABLED_TOOLS: ReadonlySet<string> = new Set<string>();

// Icon mapping from string keys to components
const ICON_MAP: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  select: SelectIcon,
  location: LocationIcon,
  'vector-line': LineIcon,
  'vector-polygon': PolygonIcon,
  cube: CubeIcon,
  'magic-wand': ClusterIcon,
  plane: PlaneIcon,
  screenshot: ScreenshotIcon,
  undo: UndoIcon,
  redo: RedoIcon,
  'Floor-detection-automatisch': FloorDetectionAutomatischIcon,
  'Floor-detection-halfautomatisch': FloorDetectionHalfautomatischIcon,
  'undo-dot': UndoDotIcon,
  'Room-detection-automatisch': RoomDetectionAutomatischIcon,
  'Room-detection-halfautomatisch': RoomDetectionHalfautomatischIcon,
  'Room-detection-manueel': RoomDetectionManueelIcon,
  'Surface-detection-automatisch': SurfaceDetectionAutomatischIcon,
  'Surface-detection-manueel': SurfaceDetectionmanueelIcon,
  Beam_automatisch: BeamAutomatischIcon,
  Column_automatisch: ColumnAutomatischIcon,
  Column: ColumnIcon,
  Beam: BeamIcon,
  skeleton: WallSketchIcon,
  'wall-create': WallCreateIcon,
  'wall-edit': WallEditIcon,
  door: DoorIcon,
  chair: ChairIcon,
  room: RoomIcon,
  'merge-rooms': MergeRoomsIcon,
  ruler: RulerIcon,
};

/**
 * Tool object from persona system
 * Subset of ITool interface from potree_fork
 */
interface ToolObject {
  name: string;
  enabled: boolean;
  /** Optional dynamic toolbar icon key, overriding the static config icon. */
  getToolbarIcon?: () => string;
}

interface UndoRedoTool extends ToolObject {
  undoRedo?: unknown;
  performUndo: () => void;
  performRedo: () => void;
}

/**
 * Tool change event from persona system
 */
interface ToolChangeEvent {
  type: string;
  tool?: ToolObject;
}

/**
 * Toolbar element from persona config
 */
interface ToolConfig {
  id: string;
  icon?: string;
  name?: string;
  disabledTooltip?: string;
  defaultEnabled?: boolean;
  defaultDisabled?: boolean;
  [key: string]: unknown;
}

// Toolbar element type enum (should match the enum from potree_fork)
enum ToolbarElementType {
  TOOL = 'tool',
  SPACER = 'spacer',
  ACTION = 'action',
}

type ToolbarElement = { type: ToolbarElementType.TOOL; config: ToolConfig } | { type: ToolbarElementType.SPACER; size?: 'small' | 'medium' | 'large' } | { type: ToolbarElementType.ACTION; id: string; icon?: string; tooltip?: string };

/**
 * Toolbar - Displays toolbar elements from the active persona
 *
 * Renders the complete toolbar including tools, spacers, and action buttons
 * based on the active persona's toolbar element configuration.
 */
export const Toolbar: FC = () => {
  const { viewer } = useContext(RendererContext);
  const { activePersona, currentMode, isReady } = usePersonaManager(viewer);
  const [toolStates, setToolStates] = useState<Record<string, boolean>>({});
  const [undoRedoState, setUndoRedoState] = useState({ canUndo: false, canRedo: false });
  // Bumped when a tool reports a dynamic icon change, forcing an icon re-read.
  const [, setIconVersion] = useState(0);

  // Get toolbar elements from active persona
  const toolbarElements = useMemo((): ToolbarElement[] => {
    if (!viewer?.personaManager || !activePersona) return [];

    const persona = viewer.personaManager.getPersona(activePersona.name);
    if (!persona) return [];

    // Check if persona supports getToolbarElements method
    if ('getToolbarElements' in persona && typeof persona.getToolbarElements === 'function') {
      return (persona.getToolbarElements() as ToolbarElement[]) || [];
    }

    return [];
  }, [viewer, activePersona]);

  // Get tools for state tracking, keyed by their config id (the persona's
  // tools Map key) — NOT the tool's runtime `name`, which can differ from the
  // id (e.g. the layout editor's id is `layout_editor` but its name is
  // `wall_sketch_editor`). The toolbar reads active state by `config.id`, so the
  // state map must be keyed by id for the highlight to match.
  const toolEntries = useMemo((): Array<[string, ToolObject]> => {
    if (!viewer?.personaManager || !activePersona) return [];

    const persona = viewer.personaManager.getPersona(activePersona.name);
    if (!persona) return [];

    return Array.from(persona.tools.entries()) as Array<[string, ToolObject]>;
  }, [viewer, activePersona]);

  // Snapshot enabled-state from the persona's tool instances whenever the tool set changes.
  // Render-phase rather than an effect, so the toolbar paints the correct states immediately;
  // live changes afterwards arrive through the tool_enabled/tool_disabled listeners below.
  useOnValueChange(toolEntries, (entries) => {
    const states: Record<string, boolean> = {};
    entries.forEach(([id, tool]) => {
      states[id] = tool.enabled;
    });
    setToolStates(states);
  });

  // Listen for tool events to update UI
  useEffect(() => {
    if (!viewer?.personaManager || !activePersona) return;

    const persona = viewer.personaManager.getPersona(activePersona.name);
    if (!persona) return;

    const handleToolChange = (event: ToolChangeEvent) => {
      const { tool } = event;
      if (!tool) return;

      // The event carries the tool instance, whose runtime `name` may differ
      // from its config id. State is keyed by config id (the tools Map key),
      // so resolve the id by matching the instance back to its Map entry.
      const entry = Array.from(persona.tools.entries() as Iterable<[string, ToolObject]>).find(([, t]) => t === tool);
      const stateKey = entry ? entry[0] : tool.name;

      setToolStates((prev) => ({
        ...prev,
        [stateKey]: tool.enabled,
      }));
    };

    persona.addEventListener('tool_enabled', handleToolChange);
    persona.addEventListener('tool_disabled', handleToolChange);

    return () => {
      persona.removeEventListener('tool_enabled', handleToolChange);
      persona.removeEventListener('tool_disabled', handleToolChange);
    };
  }, [viewer, activePersona]);

  // Disabled tools are a projection of the active persona: `isToolAvailable` is a synchronous
  // query, and `tool_availability_changed` announces when the answers change. That is exactly
  // an external store, so it is read via useSyncExternalStore — the snapshot covers the initial
  // state, so there is no separate "seed it in an effect" step.
  //
  // `isToolAvailable` is not free (FloorplanPersona scans scene collections per tool) and
  // getSnapshot runs on every render, so the answer is cached and only recomputed when the
  // persona announced a change or the persona/mode being rendered differs.
  const disabledToolsCacheRef = useRef<{ version: number; key: string; value: ReadonlySet<string> }>({ version: 0, key: '', value: EMPTY_DISABLED_TOOLS });
  const availabilityVersionRef = useRef(0);

  const activePersonaName = activePersona?.name;
  // Availability is per-mode; usePersonaManager re-renders on `mode_changed`, which the
  // persona dispatches after updating its current mode, so this is a valid cache key.
  const currentModeId = currentMode?.id;

  const subscribeDisabledTools = useCallback(
    (onStoreChange: () => void) => {
      const persona = activePersonaName ? viewer?.personaManager?.getPersona(activePersonaName) : undefined;
      if (!persona) return () => undefined;
      const onAvailabilityChanged = () => {
        availabilityVersionRef.current += 1;
        onStoreChange();
      };
      persona.addEventListener('tool_availability_changed', onAvailabilityChanged);
      return () => persona.removeEventListener('tool_availability_changed', onAvailabilityChanged);
    },
    [viewer, activePersonaName],
  );

  const getDisabledToolsSnapshot = useCallback(() => {
    const cache = disabledToolsCacheRef.current;
    const key = `${activePersonaName ?? ''}|${currentModeId ?? ''}`;
    const version = availabilityVersionRef.current;
    if (cache.version === version && cache.key === key) return cache.value;

    const persona = activePersonaName ? viewer?.personaManager?.getPersona(activePersonaName) : undefined;
    const mode = persona?.getCurrentMode?.();
    let next: ReadonlySet<string> = EMPTY_DISABLED_TOOLS;
    if (persona && mode?.toolbarElements) {
      // Re-query per tool rather than trusting the event's coarse flags. This handles
      // defaultDisabled config AND every dynamic requirement (floor selection, wall-sketch
      // prerequisite, surface selection) in one place — coarse "clear everything when a floor
      // exists" logic would re-enable tools with unmet extra requirements.
      const disabled = new Set<string>();
      for (const el of mode.toolbarElements) {
        if (el.type === ToolbarElementType.TOOL) {
          const config = el.config as unknown as ToolConfig;
          if ('isToolAvailable' in persona && typeof persona.isToolAvailable === 'function') {
            if (!persona.isToolAvailable(config.id)) disabled.add(config.id);
          } else if (config.defaultDisabled) {
            disabled.add(config.id);
          }
        }
      }
      next = disabled;
    }

    // useSyncExternalStore requires a stable snapshot: keep the previous Set when the
    // contents are unchanged, otherwise React would re-render on every store read.
    const prev = cache.value;
    const value = next.size === prev.size && [...next].every((id) => prev.has(id)) ? prev : next;
    disabledToolsCacheRef.current = { version, key, value };
    return value;
  }, [viewer, activePersonaName, currentModeId]);

  const disabledTools = useSyncExternalStore(subscribeDisabledTools, getDisabledToolsSnapshot, () => EMPTY_DISABLED_TOOLS);

  const handleToolClick = useCallback(
    (toolName: string) => {
      if (!viewer?.personaManager || !activePersona) return;

      const persona = viewer.personaManager.getPersona(activePersona.name);
      if (!persona) return;

      const tool = persona.getTool(toolName);
      if (!tool) return;

      if (tool.enabled) {
        return;
      }

      persona.enableTool(toolName);
    },
    [viewer, activePersona],
  );

  // Listen for tool undo/redo state changes
  useEffect(() => {
    if (!viewer?.scene) return;

    const handleUndoRedoChanged = (event: { type: string; canUndo?: boolean; canRedo?: boolean; [key: string]: unknown }) => {
      setUndoRedoState({ canUndo: !!event.canUndo, canRedo: !!event.canRedo });
    };

    // Also reset when tools are disabled (history is cleared)
    const handleToolDisabled = () => {
      setUndoRedoState({ canUndo: false, canRedo: false });
    };

    viewer.scene.addEventListener('tool_undo_redo_changed', handleUndoRedoChanged);
    viewer.scene.addEventListener('tool_disabled', handleToolDisabled);

    return () => {
      viewer.scene.removeEventListener('tool_undo_redo_changed', handleUndoRedoChanged);
      viewer.scene.removeEventListener('tool_disabled', handleToolDisabled);
    };
  }, [viewer]);

  // Re-render when a tool swaps its dynamic toolbar icon (e.g. the manual
  // structure tool's column/beam toggle).
  useEffect(() => {
    if (!viewer?.scene) return;

    const handleIconChanged = () => setIconVersion((v) => v + 1);
    viewer.scene.addEventListener('tool_icon_changed', handleIconChanged);

    return () => {
      viewer.scene.removeEventListener('tool_icon_changed', handleIconChanged);
    };
  }, [viewer]);

  // Get the active tool that has undo/redo support
  const getActiveToolWithUndoRedo = useCallback(() => {
    if (!viewer?.personaManager || !activePersona) return null;
    const persona = viewer.personaManager.getPersona(activePersona.name);
    if (!persona) return null;

    const enabledTools = Array.from(persona.tools.values()).filter((t: ToolObject) => t.enabled);
    // Find the first enabled tool that has an undoRedo manager
    return enabledTools.find((t: ToolObject) => (t as UndoRedoTool).undoRedo) || null;
  }, [viewer, activePersona]);

  const handleUndo = useCallback(() => {
    const tool = getActiveToolWithUndoRedo() as UndoRedoTool | null;
    if (tool) tool.performUndo();
  }, [getActiveToolWithUndoRedo]);

  const handleRedo = useCallback(() => {
    const tool = getActiveToolWithUndoRedo() as UndoRedoTool | null;
    if (tool) tool.performRedo();
  }, [getActiveToolWithUndoRedo]);

  if (!isReady || !activePersona || toolbarElements.length === 0) {
    return null;
  }

  // Tool instances keyed by config id, so the render can ask a tool for a
  // dynamic icon (getToolbarIcon) that overrides its static config icon.
  const toolInstancesById = new Map(toolEntries);

  // Render toolbar elements
  return (
    <div className="flex gap-2 items-center">
      {toolbarElements.map((element, index) => {
        // Render spacer
        if (element.type === ToolbarElementType.SPACER) {
          const sizeClass = element.size === 'large' ? 'w-8' : element.size === 'medium' ? 'w-4' : 'w-2';
          return <div key={`spacer-${index}`} className={sizeClass} />;
        }

        // Render action button (undo/redo)
        if (element.type === ToolbarElementType.ACTION) {
          const IconComponent = element.icon ? ICON_MAP[element.icon] : undefined;
          const isDisabled = element.id === 'undo' ? !undoRedoState.canUndo : element.id === 'redo' ? !undoRedoState.canRedo : false;

          const onClick = element.id === 'undo' ? handleUndo : element.id === 'redo' ? handleRedo : undefined;

          // Skip rendering if icon or onClick is missing
          if (!IconComponent || !onClick) {
            return null;
          }

          const toolButton = <ToolButton icon={IconComponent} name={element.tooltip || element.id} active={false} disabled={isDisabled} onClick={onClick} />;

          // Only wrap in Tooltip if we have a tooltip
          if (element.tooltip) {
            return (
              <Tooltip key={`action-${element.id}`} message={<T _str={element.tooltip} swc />} delay={0}>
                {toolButton}
              </Tooltip>
            );
          }

          return <div key={`action-${element.id}`}>{toolButton}</div>;
        }

        // Render tool button
        if (element.type === ToolbarElementType.TOOL) {
          const config = element.config;
          // A tool may report a dynamic icon (e.g. the manual structure tool's
          // column/beam toggle); fall back to the static config icon.
          const iconKey = toolInstancesById.get(config.id)?.getToolbarIcon?.() ?? config.icon;
          const IconComponent = iconKey ? ICON_MAP[iconKey] : undefined;

          if (!IconComponent) {
            console.warn(`No icon found for tool: ${config.id} (icon: ${iconKey})`);
            return null;
          }

          const isDisabled = disabledTools.has(config.id);

          return (
            <ToolButton
              key={config.id}
              icon={IconComponent}
              name={config.name || config.id}
              active={toolStates[config.id] === true}
              disabled={isDisabled}
              disabledTooltip={isDisabled ? config.disabledTooltip : undefined}
              onClick={() => handleToolClick(config.id)}
            />
          );
        }

        return null;
      })}
    </div>
  );
};
